import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { Sparkles, Send, Plus, MessageSquare, CheckCircle2, XCircle, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired: 'bg-slate-100 text-slate-500 border-slate-200',
  booking_confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
  quote_cancelled: 'bg-rose-100 text-rose-600 border-rose-200',
};

const AIQuoteAssistant = () => {
  const navigate = useNavigate();
  const { buildPath } = useTenant();
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [thread, setThread] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => { fetchThreads(); }, []);
  useEffect(() => { if (activeThreadId) fetchThread(activeThreadId); }, [activeThreadId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.messages]);

  const fetchThreads = async () => {
    try {
      setLoadingThreads(true);
      const res = await api.get('/ai-quotes/threads');
      setThreads(res.data);
      if (res.data.length && !activeThreadId) setActiveThreadId(res.data[0].thread_id);
    } catch (e) {
      toast.error('Could not load chat threads');
    } finally {
      setLoadingThreads(false);
    }
  };

  const fetchThread = async (id) => {
    try {
      const res = await api.get(`/ai-quotes/threads/${id}`);
      setThread(res.data);
    } catch (e) {
      toast.error('Could not load chat');
    }
  };

  const startNewThread = async () => {
    try {
      const res = await api.post('/ai-quotes/threads');
      await fetchThreads();
      setActiveThreadId(res.data.thread_id);
    } catch (e) {
      toast.error('Could not start a new chat');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending || !activeThreadId) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    setThread((prev) => prev && ({
      ...prev,
      messages: [...prev.messages, { role: 'user', content: text, created_at: new Date().toISOString() }],
    }));
    try {
      const res = await api.post(`/ai-quotes/threads/${activeThreadId}/messages`, { message: text });
      setThread((prev) => prev && ({
        ...prev,
        collected_slots: res.data.collected_slots,
        quotation_id: res.data.quotation_id,
        share_token: res.data.share_token,
        status: res.data.status,
        messages: [...prev.messages, { role: 'assistant', content: res.data.reply, created_at: new Date().toISOString() }],
      }));
      fetchThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'The assistant could not respond, please try again');
    } finally {
      setSending(false);
    }
  };

  const closeThread = async (status) => {
    try {
      await api.post(`/ai-quotes/threads/${activeThreadId}/close`, { status });
      toast.success(status === 'booking_confirmed' ? 'Marked as booking confirmed' : 'Marked as cancelled');
      fetchThreads();
      fetchThread(activeThreadId);
    } catch (e) {
      toast.error('Could not update thread status');
    }
  };

  const renderMessageContent = (content) => {
    const linkMatch = content.match(/\/quote\/[A-Za-z0-9_\-]+/);
    if (!linkMatch) return <p className="whitespace-pre-wrap text-sm">{content}</p>;
    const before = content.slice(0, linkMatch.index);
    const after = content.slice(linkMatch.index + linkMatch[0].length);
    const shareToken = linkMatch[0].replace('/quote/', '');
    return (
      <div className="text-sm whitespace-pre-wrap">
        {before}
        <a
          data-testid="ai-chat-quote-link"
          href={`${window.location.origin}${linkMatch[0]}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-semibold text-indigo-600 underline hover:text-indigo-800"
        >
          View quote <ExternalLink className="h-3 w-3" />
        </a>
        <button
          data-testid="ai-chat-copy-link-btn"
          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/quote/${shareToken}`); toast.success('Link copied'); }}
          className="ml-2 text-xs text-slate-500 underline hover:text-slate-700"
        >
          copy link
        </button>
        {after}
      </div>
    );
  };

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-7rem-88px)] md:h-[calc(100vh-7rem)] md:flex-row gap-4" data-testid="ai-quote-assistant-page">
        {/* Thread list */}
        <Card className="w-full md:w-72 md:flex-shrink-0 max-h-48 md:max-h-none flex flex-col min-h-0">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              <h2 className="font-semibold text-base">Instant Quote AI</h2>
            </div>
            <Button size="sm" data-testid="new-ai-chat-btn" onClick={startNewThread}>
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {loadingThreads ? (
              <div className="p-4 text-sm text-slate-500">Loading threads...</div>
            ) : threads.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No chats yet. Start a new one!</div>
            ) : (
              threads.map((t) => (
                <button
                  key={t.thread_id}
                  data-testid={`ai-thread-item-${t.thread_id}`}
                  onClick={() => setActiveThreadId(t.thread_id)}
                  className={`w-full text-left p-3 border-b hover:bg-slate-50 flex items-start gap-2 ${activeThreadId === t.thread_id ? 'bg-indigo-50' : ''}`}
                >
                  <MessageSquare className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{t.quote_reference}</div>
                    <Badge variant="outline" className={`text-[10px] mt-1 ${STATUS_STYLES[t.status] || ''}`}>{t.status.replace('_', ' ')}</Badge>
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Chat panel */}
        <Card className="flex-1 flex flex-col min-h-0">
          {!activeThreadId || !thread ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              Start a new chat to build an instant quote
            </div>
          ) : (
            <>
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <div className="font-semibold">{thread.quote_reference}</div>
                  <Badge variant="outline" className={`text-xs ${STATUS_STYLES[thread.status] || ''}`} data-testid="ai-thread-status-badge">
                    {thread.status.replace('_', ' ')}
                  </Badge>
                </div>
                {thread.status === 'active' && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid="mark-booking-confirmed-btn" onClick={() => closeThread('booking_confirmed')}>
                      <CheckCircle2 className="h-4 w-4 mr-1 text-blue-600" /> Booking Confirmed
                    </Button>
                    <Button size="sm" variant="outline" data-testid="mark-quote-cancelled-btn" onClick={() => closeThread('quote_cancelled')}>
                      <XCircle className="h-4 w-4 mr-1 text-rose-600" /> Cancel
                    </Button>
                  </div>
                )}
                {thread.quotation_id && (
                  <Button size="sm" variant="secondary" data-testid="view-generated-quotation-btn" onClick={() => navigate(buildPath(`/quotations/${thread.quotation_id}`))}>
                    View Quotation
                  </Button>
                )}
              </div>

              <CardContent className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3" data-testid="ai-chat-messages">
                {thread.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      data-testid={`ai-chat-message-${m.role}-${i}`}
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-800'}`}
                    >
                      {renderMessageContent(m.content)}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="rounded-2xl px-4 py-2 bg-slate-100 text-slate-500 flex items-center gap-2 text-sm">
                      <Loader2 className="h-3 w-3 animate-spin" /> Thinking...
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </CardContent>

              <div className="p-3 border-t flex gap-2">
                <Input
                  data-testid="ai-chat-input"
                  value={input}
                  disabled={thread.status !== 'active' || sending}
                  placeholder={thread.status !== 'active' ? 'This chat is closed' : 'Type your message...'}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
                />
                <Button data-testid="ai-chat-send-btn" onClick={sendMessage} disabled={thread.status !== 'active' || sending || !input.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default AIQuoteAssistant;
